import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import {
  UserGroupIcon,
  PlusIcon,
  MapPinIcon,
  ArrowLeftIcon,
  CalendarIcon,
  ChartBarIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  TrashIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { Button, Input, EmptyState } from '../components/ui';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

function getHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };
}

async function apiFetch(path: string, options?: RequestInit) {
  const res = await fetch(`${API_URL}/cooperatives${path}`, {
    headers: getHeaders(),
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(err.message || 'Request failed');
  }
  return res.json();
}

// ---------- Chart colors ----------

const CHART_COLORS = [
  '#16a34a', '#2563eb', '#d97706', '#dc2626', '#7c3aed',
  '#0891b2', '#c026d3', '#059669', '#ea580c', '#4f46e5',
];

// ---------- Types ----------

interface Cooperative {
  id: string;
  name: string;
  description: string | null;
  county: string | null;
  ward: string | null;
  registration_number: string | null;
  status: string;
  member_count: string;
  farm_count: string;
  created_at: string;
}

interface CooperativeDetail extends Cooperative {
  members: Array<{
    id: string;
    user_id: string;
    user_name: string;
    user_email: string;
    role: string;
    joined_at: string;
  }>;
  farms: Array<{
    id: string;
    farm_id: string;
    farm_name: string;
    area_hectares: number;
    crop_type: string | null;
    boundary_geojson: string | null;
  }>;
  recentActivities: Array<{
    id: string;
    activity_type: string;
    title: string;
    description: string | null;
    scheduled_date: string | null;
    status: string;
    created_by_name: string | null;
    created_at: string;
  }>;
}

interface CooperativeStats {
  memberCount: number;
  farmCount: number;
  totalArea: number;
  cropDistribution: Array<{ cropType: string; count: number; totalArea: number }>;
}

// ---------- Sub-components ----------

function CooperativeCard({
  coop,
  onSelect,
}: {
  coop: Cooperative;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      onClick={() => onSelect(coop.id)}
      className="w-full text-left bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300"
    >
      <div className="h-28 bg-gradient-to-br from-kenagri-600 via-kenagri-500 to-kenagri-700 relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-40" />
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/20 rounded-full blur-2xl" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
            <UserGroupIcon className="h-6 w-6 text-white" />
          </div>
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
          <span className="bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold text-kenagri-700 shadow-soft">
            {coop.member_count} members
          </span>
          <span className="bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold text-blue-700 shadow-soft">
            {coop.farm_count} farms
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 truncate">{coop.name}</h3>
        <div className="flex items-center gap-2 mt-1">
          {coop.county && (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <MapPinIcon className="h-3.5 w-3.5" />
              {coop.county}
              {coop.ward ? `, ${coop.ward}` : ''}
            </span>
          )}
        </div>
        {coop.description && (
          <p className="text-sm text-gray-500 mt-2 line-clamp-2">{coop.description}</p>
        )}
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center text-xs text-gray-400">
          <CalendarIcon className="h-4 w-4 mr-1.5" />
          Created {format(new Date(coop.created_at), 'MMM d, yyyy')}
        </div>
      </div>
    </button>
  );
}

function CreateCooperativeForm({ onSuccess }: { onSuccess: () => void }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    county: '',
    ward: '',
    registrationNumber: '',
  });

  const mutation = useMutation({
    mutationFn: (data: typeof form) =>
      apiFetch('', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      toast.success('Cooperative created successfully');
      onSuccess();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to create cooperative');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    mutation.mutate(form);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-5">
      <Input
        label="Cooperative Name"
        placeholder="e.g. Kiambu Dairy Farmers"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        required
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
        <textarea
          className="w-full bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 transition-all duration-200 py-3 px-4 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 hover:border-gray-300"
          rows={3}
          placeholder="Brief description of the cooperative..."
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="County"
          placeholder="e.g. Kiambu"
          value={form.county}
          onChange={(e) => setForm({ ...form, county: e.target.value })}
        />
        <Input
          label="Ward"
          placeholder="e.g. Kabete"
          value={form.ward}
          onChange={(e) => setForm({ ...form, ward: e.target.value })}
        />
      </div>
      <Input
        label="Registration Number"
        placeholder="e.g. COOP/2025/001"
        value={form.registrationNumber}
        onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })}
      />
      <div className="pt-2">
        <Button type="submit" variant="primary" size="lg" loading={mutation.isPending}>
          Create Cooperative
        </Button>
      </div>
    </form>
  );
}

function ActivityTypeTag({ type }: { type: string }) {
  const styles: Record<string, string> = {
    meeting: 'bg-blue-100 text-blue-700',
    harvest: 'bg-amber-100 text-amber-700',
    input_purchase: 'bg-purple-100 text-purple-700',
    sale: 'bg-green-100 text-green-700',
    training: 'bg-cyan-100 text-cyan-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[type] || 'bg-gray-100 text-gray-600'}`}>
      {type.replace('_', ' ')}
    </span>
  );
}

function StatusTag({ status }: { status: string }) {
  const styles: Record<string, string> = {
    scheduled: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function CooperativeDetailView({
  coopId,
  onBack,
}: {
  coopId: string;
  onBack: () => void;
}) {
  const [detailTab, setDetailTab] = useState<'overview' | 'members' | 'farms' | 'activities'>('overview');
  const queryClient = useQueryClient();

  const { data: detail, isLoading } = useQuery<{ data: CooperativeDetail }>({
    queryKey: ['cooperative', coopId],
    queryFn: () => apiFetch(`/${coopId}`),
  });

  const { data: statsData } = useQuery<{ data: CooperativeStats }>({
    queryKey: ['cooperative-stats', coopId],
    queryFn: () => apiFetch(`/${coopId}/stats`),
  });

  const { data: activitiesData } = useQuery({
    queryKey: ['cooperative-activities', coopId],
    queryFn: () => apiFetch(`/${coopId}/activities`),
  });

  const removeFarmMutation = useMutation({
    mutationFn: (farmId: string) =>
      apiFetch(`/${coopId}/farms/${farmId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Farm removed');
      queryClient.invalidateQueries({ queryKey: ['cooperative', coopId] });
      queryClient.invalidateQueries({ queryKey: ['cooperative-stats', coopId] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/${coopId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Member removed');
      queryClient.invalidateQueries({ queryKey: ['cooperative', coopId] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Activity creation
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [activityForm, setActivityForm] = useState({
    activityType: 'meeting' as 'meeting' | 'harvest' | 'input_purchase' | 'sale' | 'training',
    title: '',
    description: '',
    scheduledDate: '',
  });

  const createActivityMutation = useMutation({
    mutationFn: (data: typeof activityForm) =>
      apiFetch(`/${coopId}/activities`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      toast.success('Activity created');
      setShowActivityForm(false);
      setActivityForm({ activityType: 'meeting', title: '', description: '', scheduledDate: '' });
      queryClient.invalidateQueries({ queryKey: ['cooperative-activities', coopId] });
      queryClient.invalidateQueries({ queryKey: ['cooperative', coopId] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const coop = detail?.data;
  const stats = statsData?.data;
  const activities = (activitiesData as any)?.data || coop?.recentActivities || [];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-gray-200 rounded w-48 animate-pulse" />
        <div className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!coop) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>Cooperative not found.</p>
        <Button variant="ghost" className="mt-4" onClick={onBack}>Go back</Button>
      </div>
    );
  }

  const tabItems = [
    { key: 'overview' as const, label: 'Overview', icon: ChartBarIcon },
    { key: 'members' as const, label: 'Members', icon: UsersIcon },
    { key: 'farms' as const, label: 'Farms', icon: MapPinIcon },
    { key: 'activities' as const, label: 'Activities', icon: ClipboardDocumentListIcon },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900">{coop.name}</h2>
          <p className="text-sm text-gray-500">
            {coop.county}{coop.ward ? `, ${coop.ward}` : ''}
            {coop.registration_number ? ` -- ${coop.registration_number}` : ''}
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
          coop.status === 'active' ? 'bg-green-100 text-green-700' :
          coop.status === 'suspended' ? 'bg-amber-100 text-amber-700' :
          'bg-gray-100 text-gray-500'
        }`}>
          {coop.status}
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 overflow-x-auto">
        {tabItems.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setDetailTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              detailTab === tab.key
                ? 'bg-white text-kenagri-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {detailTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-kenagri-50 to-white rounded-xl p-4 border border-kenagri-100">
              <p className="text-sm font-medium text-kenagri-600">Members</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.memberCount ?? coop.member_count}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 border border-blue-100">
              <p className="text-sm font-medium text-blue-600">Farms</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.farmCount ?? coop.farm_count}</p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-4 border border-amber-100">
              <p className="text-sm font-medium text-amber-600">Total Area</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.totalArea?.toFixed(1) ?? '0'} ha</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 border border-purple-100">
              <p className="text-sm font-medium text-purple-600">Activities</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{activities.length}</p>
            </div>
          </div>

          {/* Crop distribution chart */}
          {stats?.cropDistribution && stats.cropDistribution.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Crop Distribution</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.cropDistribution.map((c) => ({
                        name: c.cropType || 'Unknown',
                        value: c.totalArea,
                        count: c.count,
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      dataKey="value"
                      label={({ name, value }: { name: string; value: number }) => `${name} (${value.toFixed(1)} ha)`}
                    >
                      {stats.cropDistribution.map((_, index) => (
                        <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, _name: string, props: any) =>
                        [`${value.toFixed(1)} ha (${props.payload.count} farms)`, 'Area']
                      }
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Recent activities timeline */}
          {coop.recentActivities && coop.recentActivities.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
              <div className="space-y-4">
                {coop.recentActivities.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 pb-4 border-b border-gray-50 last:border-0 last:pb-0">
                    <div className="w-2 h-2 rounded-full bg-kenagri-500 mt-2 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-900 text-sm">{activity.title}</p>
                        <ActivityTypeTag type={activity.activity_type} />
                        <StatusTag status={activity.status} />
                      </div>
                      {activity.description && (
                        <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{activity.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {activity.scheduled_date
                          ? format(new Date(activity.scheduled_date), 'MMM d, yyyy')
                          : format(new Date(activity.created_at), 'MMM d, yyyy')}
                        {activity.created_by_name ? ` -- by ${activity.created_by_name}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {detailTab === 'members' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {coop.members.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{member.user_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{member.user_email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        member.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                        member.role === 'officer' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {member.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {format(new Date(member.joined_at), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => removeMemberMutation.mutate(member.user_id)}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                        title="Remove member"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {coop.members.length === 0 && (
            <div className="py-12 text-center text-gray-500 text-sm">No members found.</div>
          )}
        </div>
      )}

      {detailTab === 'farms' && (
        <div className="space-y-4">
          {coop.farms.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {coop.farms.map((farm) => (
                <div
                  key={farm.id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-card p-5 hover:shadow-card-hover transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 truncate">{farm.farm_name}</h4>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {farm.crop_type || 'Unknown crop'}
                      </p>
                    </div>
                    <button
                      onClick={() => removeFarmMutation.mutate(farm.farm_id)}
                      className="text-gray-400 hover:text-red-500 transition-colors p-1 -mt-1 -mr-1"
                      title="Remove farm"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <MapPinIcon className="h-4 w-4" />
                      {farm.area_hectares?.toFixed(1) ?? '?'} ha
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-card py-12 text-center text-gray-500 text-sm">
              No farms linked yet. Members can link their farms to this cooperative.
            </div>
          )}
        </div>
      )}

      {detailTab === 'activities' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              leftIcon={<PlusIcon className="h-4 w-4" />}
              onClick={() => setShowActivityForm(!showActivityForm)}
            >
              New Activity
            </Button>
          </div>

          {showActivityForm && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Create Activity</h4>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!activityForm.title.trim()) {
                    toast.error('Title is required');
                    return;
                  }
                  createActivityMutation.mutate(activityForm);
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Type</label>
                    <select
                      className="w-full bg-white border border-gray-200 rounded-xl text-gray-900 py-3 px-4 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20"
                      value={activityForm.activityType}
                      onChange={(e) =>
                        setActivityForm({ ...activityForm, activityType: e.target.value as typeof activityForm.activityType })
                      }
                    >
                      <option value="meeting">Meeting</option>
                      <option value="harvest">Harvest</option>
                      <option value="input_purchase">Input Purchase</option>
                      <option value="sale">Sale</option>
                      <option value="training">Training</option>
                    </select>
                  </div>
                  <Input
                    label="Scheduled Date"
                    type="datetime-local"
                    value={activityForm.scheduledDate}
                    onChange={(e) =>
                      setActivityForm({ ...activityForm, scheduledDate: e.target.value })
                    }
                  />
                </div>
                <Input
                  label="Title"
                  placeholder="e.g. Monthly Members Meeting"
                  value={activityForm.title}
                  onChange={(e) =>
                    setActivityForm({ ...activityForm, title: e.target.value })
                  }
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                  <textarea
                    className="w-full bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 transition-all duration-200 py-3 px-4 focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 hover:border-gray-300"
                    rows={2}
                    placeholder="Optional description..."
                    value={activityForm.description}
                    onChange={(e) =>
                      setActivityForm({ ...activityForm, description: e.target.value })
                    }
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="submit" variant="primary" size="sm" loading={createActivityMutation.isPending}>
                    Create
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowActivityForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Activities list */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-card divide-y divide-gray-50">
            {activities.length > 0 ? (
              activities.map((activity: any) => (
                <div key={activity.id} className="px-6 py-4 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-kenagri-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CalendarIcon className="h-5 w-5 text-kenagri-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-gray-900 text-sm">{activity.title}</p>
                      <ActivityTypeTag type={activity.activity_type} />
                      <StatusTag status={activity.status} />
                    </div>
                    {activity.description && (
                      <p className="text-sm text-gray-500 mt-1">{activity.description}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {activity.scheduled_date
                        ? `Scheduled: ${format(new Date(activity.scheduled_date), 'MMM d, yyyy h:mm a')}`
                        : format(new Date(activity.created_at), 'MMM d, yyyy')}
                      {activity.created_by_name ? ` -- by ${activity.created_by_name}` : ''}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-12 text-center text-gray-500 text-sm">
                No activities yet. Create your first activity above.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Main Page ----------

export default function Cooperatives() {
  const [tab, setTab] = useState<'list' | 'create'>('list');
  const [selectedCoopId, setSelectedCoopId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{ data: Cooperative[] }>({
    queryKey: ['cooperatives'],
    queryFn: () => apiFetch(''),
  });

  const cooperatives = data?.data || [];

  // Show detail view if a cooperative is selected
  if (selectedCoopId) {
    return (
      <div className="p-6">
        <CooperativeDetailView
          coopId={selectedCoopId}
          onBack={() => setSelectedCoopId(null)}
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cooperatives</h1>
          <p className="text-gray-500 mt-1">Manage your group farming cooperatives</p>
        </div>
        <Button
          variant="primary"
          size="md"
          leftIcon={<PlusIcon className="h-5 w-5" />}
          onClick={() => setTab(tab === 'create' ? 'list' : 'create')}
        >
          {tab === 'create' ? 'View List' : 'New Cooperative'}
        </Button>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        <button
          onClick={() => setTab('list')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            tab === 'list'
              ? 'bg-white text-kenagri-700 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <BuildingOfficeIcon className="h-4 w-4" />
          My Cooperatives
        </button>
        <button
          onClick={() => setTab('create')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            tab === 'create'
              ? 'bg-white text-kenagri-700 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <PlusIcon className="h-4 w-4" />
          Create Cooperative
        </button>
      </div>

      {tab === 'create' && (
        <CreateCooperativeForm
          onSuccess={() => {
            setTab('list');
            queryClient.invalidateQueries({ queryKey: ['cooperatives'] });
          }}
        />
      )}

      {tab === 'list' && (
        <>
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden animate-pulse">
                  <div className="h-28 bg-gray-200" />
                  <div className="p-4 space-y-3">
                    <div className="h-5 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-100 rounded w-1/2" />
                    <div className="h-4 bg-gray-100 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : cooperatives.length > 0 ? (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                <div className="bg-gradient-to-br from-kenagri-50 to-white rounded-xl p-4 border border-kenagri-100">
                  <p className="text-sm font-medium text-kenagri-600">Cooperatives</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{cooperatives.length}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 border border-blue-100">
                  <p className="text-sm font-medium text-blue-600">Total Members</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {cooperatives.reduce((sum, c) => sum + parseInt(String(c.member_count), 10), 0)}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 border border-purple-100">
                  <p className="text-sm font-medium text-purple-600">Total Farms</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {cooperatives.reduce((sum, c) => sum + parseInt(String(c.farm_count), 10), 0)}
                  </p>
                </div>
              </div>

              {/* Cooperative cards grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {cooperatives.map((coop) => (
                  <CooperativeCard
                    key={coop.id}
                    coop={coop}
                    onSelect={setSelectedCoopId}
                  />
                ))}
              </div>
            </>
          ) : (
            <EmptyState
              icon={UserGroupIcon}
              title="No cooperatives yet"
              description="Create or join a cooperative to manage group farming activities, share resources, and track aggregate production."
              action={{
                label: 'Create Cooperative',
                onClick: () => setTab('create'),
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
